/**
 * PublicHeader v2
 *
 * Architecture :
 * - Nav 64px sticky : burger | logo | pill MCP (centre) | [user/connexion] [S'abonner]
 * - Menu overlay pleine page (burger toggle) : 2 colonnes desktop / 1 colonne mobile
 *   - Gauche  : liens principaux Fraunces + liens secondaires + CTAs mobiles
 *   - Droite  : citation éditoriale + 2 encarts CTA (S'abonner / Créer compte)
 * - Popover user conservé à droite (connecté)
 *
 * Logique CTA :
 * - Non connecté        → "Connexion" ghost + "S'abonner" violet
 * - Connecté, free      → avatar + "Passer à Pro" outline
 * - Connecté, paid      → avatar uniquement
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { CLIENTS } from '@/components/jurisprudence/MCPBadge';
import { MenuOverlay } from '@/components/jurisprudence/MenuOverlay';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { HreflangLinks } from '@/components/HreflangLinks';
import { useTranslation } from 'react-i18next';
import { isDesktop } from '@/AppMode';

interface PublicHeaderProps {
  onLogoClick?: () => void;
}

const ACCOUNT_LINKS = [
  {
    to:    '/settings',
    labelKey: 'header.account_links.profile',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
      </svg>
    ),
  },
  {
    to:    '/settings/subscription',
    labelKey: 'header.account_links.subscription',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
      </svg>
    ),
  },
  {
    to:    '/settings/api-key',
    labelKey: 'header.account_links.api_key',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
      </svg>
    ),
  },
  {
    to:    '/settings/invoices',
    labelKey: 'header.account_links.invoices',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
      </svg>
    ),
  },
];

// ── Watermark SVG Legal Graph (décoratif, fond menu) ──────────────────────────
const _LegalGraphWatermark = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <circle cx="50" cy="32" r="7" fill="#1DD3A0"/>
    <line x1="50" y1="39" x2="50" y2="78" stroke="#1c3566" strokeWidth="3" strokeLinecap="round"/>
    <line x1="26" y1="46" x2="74" y2="46" stroke="#1c3566" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="26" cy="46" r="4" fill="#1c3566"/>
    <circle cx="74" cy="46" r="4" fill="#1c3566"/>
    <path d="M14 66 Q26 73 38 66" stroke="#1c3566" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M62 66 Q74 73 86 66" stroke="#1c3566" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M40 82 Q50 87 60 82" stroke="#1c3566" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    <circle cx="24" cy="18" r="4" fill="#6d4af5"/>
    <circle cx="46" cy="10" r="3.5" fill="#2b5ce6"/>
    <circle cx="78" cy="20" r="4" fill="#e53e3e" opacity=".7"/>
    <circle cx="84" cy="44" r="3.5" fill="#2b5ce6"/>
    <line x1="50" y1="25" x2="46" y2="14" stroke="#2b5ce6" strokeWidth="1.5"/>
    <line x1="50" y1="25" x2="24" y2="22" stroke="#6d4af5" strokeWidth="1.5"/>
    <line x1="50" y1="25" x2="78" y2="24" stroke="#6d4af5" strokeWidth="1.5"/>
    <line x1="78" y1="24" x2="84" y2="44" stroke="#2b5ce6" strokeWidth="1.5"/>
  </svg>
);

// ── Burger icon ───────────────────────────────────────────────────────────────
const BurgerIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M4 6h16M4 12h16M4 18h16"/>
  </svg>
);

// ── Close icon ────────────────────────────────────────────────────────────────
const _CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M6 18L18 6M6 6l12 12"/>
  </svg>
);

// ── Menu overlay ──────────────────────────────────────────────────────────────
interface _MenuOverlayProps {
  open:     boolean;
  onClose:  () => void;
  user:     ReturnType<typeof usePublicAuth>['user'];
  isPaid:   boolean;
}


// ── PublicHeader ──────────────────────────────────────────────────────────────
export const PublicHeader = ({ onLogoClick }: PublicHeaderProps) => {
  const { t } = useTranslation();
  const { user, logout, subStatus, getToken } = usePublicAuth();
  const navigate                              = useNavigate();
  const [menuOpen, setMenuOpen]               = useState(false);
  const [userOpen, setUserOpen]               = useState(false);
  const [hasMcpKey, setHasMcpKey]             = useState<boolean | null>(null);
  const userRef    = useRef<HTMLDivElement>(null);

  const isPaid           = subStatus?.plan === 'paid' && subStatus?.has_subscription;
  const showSubscribeCta = !isPaid;

  // Fetch statut clé MCP quand l'utilisateur est paid
  useEffect(() => {
    if (!isPaid || !user) { setHasMcpKey(null); return; }
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const res  = await fetch('/api/auth/mcp-keys', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setHasMcpKey(!!data.has_key);
      } catch { setHasMcpKey(false); }
    })();
  }, [isPaid, user, getToken]);

  // Ferme le popover user si clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Ferme le menu sur ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMenuOpen(false); setUserOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    setUserOpen(false);
    await logout();
    navigate('/');
  };

  const handleLogoClick = () => {
    setMenuOpen(false);
    onLogoClick?.();
  };

  return (
    <>
      <HreflangLinks />
      {/* ── Barre sticky 64px ── */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur-md z-40 transition-shadow">
        <div className="w-full px-4 sm:px-6 h-16 flex items-center justify-between gap-4">

          {/* ── Gauche : burger + logo ── */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMenuOpen(v => !v)}
              className="flex flex-col gap-[4.5px] p-2.5 rounded-lg hover:bg-accent transition-colors border border-transparent hover:border-border/50"
              aria-label={t('common.menu')}
            >
              <BurgerIcon />
            </button>

            <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2">
              <Logo className="h-12 w-auto" />
              <span className="font-serif font-bold text-base tracking-tight hidden sm:inline">
                SmartLawyer
              </span>
            </Link>
          </div>

          {/* ── Centre : pill MCP ── */}
          <div className="hidden sm:block">
            {isPaid && hasMcpKey ? null : isPaid && hasMcpKey === false ? (
              /* Paid sans clé → lien vers settings */
              <Link
                to="/settings/api-key"
                className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-all whitespace-nowrap text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-700/60 hover:bg-violet-100 dark:hover:bg-violet-900/40 hover:border-violet-300 dark:hover:border-violet-600"
              >
                <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-600 text-white leading-none tracking-wide">
                  MCP
                </span>
                {t('header.my_mcp_key')}
              </Link>
            ) : !isPaid && !isDesktop() ? (
              /* Non-paid → pill acquisition (pas dans l'app desktop) */
              <a
                href="https://smartlawyer.ai/mcp/"
                className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-all whitespace-nowrap text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-700/60 hover:bg-violet-100 dark:hover:bg-violet-900/40 hover:border-violet-300 dark:hover:border-violet-600"
              >
                <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-600 text-white leading-none tracking-wide">
                  MCP
                </span>
                {t('header.mcp_pill')}
                <span className="flex items-center gap-0.5 pl-2 border-l border-violet-200 dark:border-violet-700/50">
                  {CLIENTS.map(c => (
                    <span key={c.id} title={c.name} className="opacity-80 hover:opacity-100 transition-opacity">
                      {c.iconHeader}
                    </span>
                  ))}
                </span>
                <svg className="w-2.5 h-2.5 text-violet-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17L17 7M17 7H7M17 7v10"/>
                </svg>
              </a>
            ) : null}
          </div>
          {/* ── Droite : user + CTA ── */}
          <div className="flex items-center gap-2">

            <LocaleSwitcher className="hidden md:block" />

            {/* Connecté → avatar + popover */}
            {user ? (
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => setUserOpen(v => !v)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={t('header.avatar_alt')}
                      className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
                      {(user.displayName || user.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm text-muted-foreground max-w-[100px] truncate">
                    {user.displayName?.split(' ')[0] || user.email?.split('@')[0]}
                  </span>
                  <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${userOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 rounded-xl border bg-card shadow-lg py-1 z-50">
                    {/* Infos + badge plan */}
                    <div className="px-4 py-3 border-b">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold truncate">{user.displayName || '—'}</p>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          isPaid
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {isPaid ? t('header.plan_pro') : t('common.free')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>

                    {/* Liens compte */}
                    <div className="py-1">
                      <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {t('header.my_account')}
                      </p>
                      {ACCOUNT_LINKS.filter(({ to }) => to !== '/settings/invoices' || isPaid).map(({ to, labelKey, icon }) => (
                        <Link key={to} to={to} onClick={() => setUserOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-accent transition-colors">
                          <span className="text-muted-foreground">{icon}</span>
                          {t(labelKey)}
                        </Link>
                      ))}
                    </div>

                    {/* CTA upgrade si free */}
                    {!isPaid && (
                      <div className="px-3 py-2 border-t">
                        <Link to="/pricing" onClick={() => setUserOpen(false)}
                          className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700 transition-colors">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z"/>
                          </svg>
                          {t('header.upgrade_price')}
                        </Link>
                      </div>
                    )}

                    {/* Contact + Déconnexion */}
                    <div className="border-t py-1">
                      <Link to="/contact" onClick={() => setUserOpen(false)}
                        className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                        {t('header.contact')}
                      </Link>
                      <button onClick={handleLogout}
                        className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                        </svg>
                        {t('common.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

            ) : (
              /* Non connecté → ghost Connexion */
              <Link to="/login"
                className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg border hover:bg-accent transition-colors">
                {t('common.login')}
              </Link>
            )}

            {/* CTA S'abonner / Passer à Pro */}
            {showSubscribeCta && !isDesktop() && (
              <Link to="/pricing"
                className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  !user
                    ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-md shadow-violet-600/20'
                    : 'border-2 border-violet-600 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/30'
                }`}>
                {!user ? t('common.subscribe') : t('header.upgrade')}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Menu overlay pleine page ── */}
      <MenuOverlay
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        isPaid={isPaid}
      />
    </>
  );
};